import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Card, 
  Typography, 
  Space, 
  Button, 
  message, 
  Spin,
  Divider,
  Tag
} from 'antd';
import { 
  ArrowLeftOutlined, 
  EditOutlined, 
  DeleteOutlined,
} from '@ant-design/icons';
import { publicationService } from '../../services/publicationService';
import { attachmentService } from '../../services/attachmentService';
import { useAuth } from '../../contexts/AuthContext';
import { Publication, Attachment, PublicationCategory } from '../../types';
import { formatLocalizedDateTime } from '../../utils/dateUtils';
import { useTranslation } from 'react-i18next';
import { tableStyles as generalTableStyles } from '../../utils/tableUtils';
import '../../styles/contentStyles.css';
import { removeContentEditable, setupDOMObserver, executeWithDelay, adjustImages } from '../../utils/domUtils';

const { Title } = Typography;

// 상세보기용 CSS 스타일 - 편집 버튼 숨김 및 표 스타일 적용
const detailStyles = `
  .preview-content .add-row-btn,
  .preview-content .delete-row-btn,
  .preview-content .table-controls {
    display: none !important;
  }
  
  .preview-content table td[contenteditable="true"] {
    cursor: default;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    pointer-events: none;
  }
  
  .preview-content table td[contenteditable="true"]:focus {
    outline: none;
    background-color: transparent;
  }
  
  .preview-content table td[contenteditable="true"]:hover {
    background-color: transparent;
  }
  
  /* 상세보기에서 모든 편집 가능 요소 비활성화 */
  .preview-content [contenteditable="true"] {
    cursor: default;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    pointer-events: none;
  }
  
  .preview-content [contenteditable="true"]:focus {
    outline: none;
    background-color: transparent;
  }
  
  .preview-content img {
    max-width: 100% !important;
    max-height: 600px !important;
    width: auto !important;
    height: auto !important;
    display: block;
    margin: 10px 0;
  }
  
  /* 중첩된 Card 스타일 제거 */
  .preview-content .ant-card {
    border: none !important;
    box-shadow: none !important;
    background: transparent !important;
  }
  
  .preview-content .ant-card-body {
    padding: 0 !important;
    background: transparent !important;
  }
  
  /* 상세보기용 스타일 클래스 적용 */
  .preview-content .title-style,
  .preview-content .pub-title-style {
    font-size: 24px !important;
    font-weight: bold !important;
    text-align: center !important;
    margin: 20px 0 16px 0 !important;
    color: #262626 !important;
    display: block !important;
    width: 100% !important;
  }
  
  .preview-content .jang-style,
  .preview-content .pub-jang-style {
    font-size: 18px !important;
    font-weight: bold !important;
    text-align: left !important;
    padding: 40px 0 0 80px !important;
    margin: 0 !important;
    color: #262626 !important;
    display: block !important;
    width: 100% !important;
    border: none !important;
    background: none !important;
    outline: none !important;
  }
  
  .preview-content .jo-style,
  .preview-content .pub-jo-style {
    padding: 16px 0 0px 40px !important;
    text-indent: -40px !important;
    font-size: 16px !important;
    font-weight: normal !important;
    margin: 0 !important;
  }
  
  .preview-content .hang-style,
  .preview-content .pub-hang-style {
    padding: 0px 0 0px 60px !important;
    text-indent: -20px !important;
    font-size: 16px !important;
    font-weight: normal !important;
    margin: 0 !important;
  }
  
  .preview-content .ho-style,
  .preview-content .pub-ho-style {
    padding: 0px 0 0px 80px !important;
    text-indent: -20px !important;
    font-size: 16px !important;
    font-weight: normal !important;
    margin: 0 !important;
  }
  
  
  ${generalTableStyles}
`;

const PublicationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { t, i18n } = useTranslation();
  const [publication, setPublication] = useState<Publication | null>(null);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(true);

  // 카테고리 번역 함수
  const getCategoryTranslation = (category: PublicationCategory | string): string => {
    switch (category) {
      case PublicationCategory.PUBLICATION:
      case '공포':
        return t('publications.categories.PUBLICATION');
      case PublicationCategory.ANNOUNCEMENT:
      case '공시':
        return t('publications.categories.ANNOUNCEMENT');
      default:
        return category;
    }
  };

  const fetchPublicationDetail = useCallback(async () => {
    if (!id) return;
    
    setLoading(true);
    try {
      const data = await publicationService.getPublication(id);
      setPublication(data);
      
      // 첨부파일 별도 조회 (에디터 이미지 제외)
      try {
        const attachmentData = await attachmentService.getPublicationAttachments(id);
        setAttachments(attachmentData);
      } catch (error) {
        // 첨부파일 조회 실패 시 빈 배열로 설정
        setAttachments([]);
      }
    } catch (error: any) {
      message.error(t('publications.DETAIL_SECTION.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    if (id) {
      fetchPublicationDetail();
    }
  }, [id, fetchPublicationDetail]);

  // 상세보기에서 contenteditable 속성 제거
  useEffect(() => {
    if (publication) {
      // 즉시 실행
      removeContentEditable();
      
      // 지연 실행
      const cleanup = executeWithDelay(removeContentEditable);
      
      // MutationObserver 설정
      const previewContent = document.querySelector('.preview-content');
      const observer = previewContent ? setupDOMObserver(previewContent, removeContentEditable, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['contenteditable']
      }) : null;

      return () => {
        cleanup();
        observer?.disconnect();
      };
    }
  }, [publication]);

  // 이미지 크기 자동 조정을 위한 useEffect
  useEffect(() => {
    if (publication) {
      // 즉시 실행
      adjustImages();
      
      // 지연 실행
      const cleanup = executeWithDelay(adjustImages, [100, 500, 1000, 2000]);

      // 윈도우 리사이즈 이벤트 리스너 추가
      const handleResize = () => {
        adjustImages();
      };

      window.addEventListener('resize', handleResize);

      // cleanup
      return () => {
        cleanup();
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [publication]);

  const handleDelete = async () => {
    if (!publication) return;
    
    try {
      await publicationService.deletePublication(publication.publication_id);
      message.success(t('publications.DETAIL_SECTION.deleteSuccess'));
      navigate('/publications');
    } catch (error: any) {
      message.error(error.response?.data?.message || t('publications.DETAIL_SECTION.deleteFailed'));
    }
  };

  const handleApprove = async () => {
    if (!publication) return;
    
    try {
      await publicationService.approvePublication(publication.publication_id);
      message.success(t('publications.DETAIL_SECTION.approveSuccess'));
      fetchPublicationDetail();
    } catch (error: any) {
      message.error(error.response?.data?.message || t('publications.DETAIL_SECTION.approveFailed'));
    }
  };

  const handleUnapprove = async () => {
    if (!publication) return;
    
    try {
      await publicationService.unapprovePublication(publication.publication_id);
      message.success(t('publications.DETAIL_SECTION.unapproveSuccess'));
      fetchPublicationDetail();
    } catch (error: any) {
      message.error(error.response?.data?.message || t('publications.DETAIL_SECTION.unapproveFailed'));
    }
  };

  const handleDownloadAttachment = async (attachment: Attachment) => {
    try {
      attachmentService.downloadAttachmentDirect(attachment.attachment_id, decodeURIComponent(attachment.file_name));
    } catch (error: any) {
      message.error(t('publications.DETAIL_SECTION.downloadFailed'));
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>{t('publications.DETAIL_SECTION.loading')}</div>
      </div>
    );
  }

  if (!publication) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Title level={3}>{t('publications.DETAIL_SECTION.notFound')}</Title>
        <Button type="primary" onClick={() => navigate('/publications')}>
          {t('publications.DETAIL_SECTION.backToList')}
        </Button>
      </div>
    );
  }

  return (
    <div>
      <style>{detailStyles}</style>
      <Card>
        {/* 헤더 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate('/publications')}
            style={{ marginRight: 16 }}
          >
            {t('publications.DETAIL_SECTION.backToList')}
          </Button>
          
          <Space>
            {isAdmin && (
              <>
                {publication.is_approved ? (
                  <Button
                    danger
                    onClick={() => {
                      if (window.confirm(t('publications.DETAIL_SECTION.unapproveConfirm'))) {
                        handleUnapprove();
                      }
                    }}
                  >
                    {t('publications.DETAIL_SECTION.unapprove')}
                  </Button>
                ) : (
                  <Button
                    type="primary"
                    onClick={() => {
                      if (window.confirm(t('publications.DETAIL_SECTION.approveConfirm'))) {
                        handleApprove();
                      }
                    }}
                  >
                    {t('publications.DETAIL_SECTION.approve')}
                  </Button>
                )}
                <Button
                  icon={<EditOutlined />}
                  onClick={() => navigate(`/publications/${publication.publication_id}/edit`)}
                  disabled={publication.is_approved}
                  title={publication.is_approved ? t('publications.DETAIL_SECTION.approvedCannotEdit') : t('publications.DETAIL_SECTION.edit')}
                >
                  {t('publications.DETAIL_SECTION.edit')}
                </Button>
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    if (publication.is_approved) {
                      message.warning(t('publications.DETAIL_SECTION.approvedCannotDelete'));
                      return;
                    }
                    if (window.confirm(t('publications.DETAIL_SECTION.deleteConfirm'))) {
                      handleDelete();
                    }
                  }}
                  disabled={publication.is_approved}
                  title={publication.is_approved ? t('publications.DETAIL_SECTION.approvedCannotDelete') : t('publications.DETAIL_SECTION.delete')}
                >
                  {t('publications.DETAIL_SECTION.delete')}
                </Button>
              </>
            )}
          </Space>
        </div>

        {/* 제목 및 메타 정보 */}
        <div style={{ marginBottom: 24 }}>
          <Title level={2}>{publication.title}</Title>
          <Space size="large" style={{ marginBottom: 16 }}>
            <Tag color={publication.category === PublicationCategory.PUBLICATION ? 'blue' : 'green'}>
              {getCategoryTranslation(publication.category)}
            </Tag>
            <span>{t('publications.DETAIL_SECTION.createdAt')}: {formatLocalizedDateTime(publication.created_at, i18n.language)}</span>
            {publication.updated_at && publication.updated_at !== publication.created_at && (
              <span>{t('publications.DETAIL_SECTION.updatedAt')}: {formatLocalizedDateTime(publication.updated_at, i18n.language)}</span>
            )}
            <span>{t('publications.DETAIL_SECTION.viewCount')}: {publication.view_count.toLocaleString()}</span>
          </Space>
        </div>

        <Divider />

        {/* 첨부파일 */}
        {attachments && attachments.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <Title level={4}>{t('publications.DETAIL_SECTION.attachments')}</Title>
            <Card>
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {attachments.map((attachment) => (
                  <div
                    key={attachment.attachment_id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px',
                      border: '1px solid #f0f0f0',
                      borderRadius: '6px',
                      backgroundColor: '#fafafa'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ fontSize: '20px' }}>📎</span>
                      <div>
                        <div style={{ fontWeight: '500', marginBottom: '4px' }}>
                          {decodeURIComponent(attachment.file_name)}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {attachmentService.formatFileSize(attachment.file_size)} • {formatLocalizedDateTime(attachment.uploaded_at, i18n.language)}
                        </div>
                      </div>
                    </div>
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => handleDownloadAttachment(attachment)}
                    >
                      {t('publications.DETAIL_SECTION.download')}
                    </Button>
                  </div>
                ))}
              </Space>
            </Card>
          </div>
        )}

        {/* 내용 */}
        <div>
          <Title level={4}>{t('publications.DETAIL_SECTION.content')}</Title>
          <div 
            className="preview-content"
            style={{ 
              backgroundColor: '#fafafa',
              border: '1px solid #e8e8e8',
              borderRadius: '8px',
              padding: '20px',
              marginTop: '16px'
            }}
            dangerouslySetInnerHTML={{ __html: publication.content }}
          />
        </div>
      </Card>
    </div>
  );
};

export default PublicationDetail; 